import { Box, Button, Card, DialogTitle, Grid, IconButton, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Filter, FilterType, OptionWithSelect } from "../../filters/FilterTypes";
import { DataGrid } from "@mui/x-data-grid";
import { client } from "../../../utils/axios-client";
import { CadenceTemplateVariant, loadPlaybooks, PlaybookStore } from "../PlaybookStore";
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import { v4 as uuid } from 'uuid';
import EditPlaybookFilter from "./EditPlaybookFilter";
import PencilOutline from 'mdi-material-ui/PencilOutline'
import TrashCan from 'mdi-material-ui/TrashCan'
import SimpleDialog from "../../common/SimpleDialog";
import PageHeader from "../../../materio/@core/components/page-header";
import ConfirmationDialog from "../../common/ConfirmationDialog";
import RenderIf, { ClaimType, ClaimValue } from "../../common/RenderIf";

interface CellType {
    row: Filter
}

const defaultColumns = [
    {
        flex: 0.65,
        field: 'description',
        minWidth: 240,
        headerName: 'Description',
        renderCell: ({ row }: CellType) => <Typography>{row.description}</Typography>
    }
]

type Props = {
    open: boolean,
    onClose: () => void
}

const VariantFilters = ({ open, onClose }: Props) => {
    const [editFilterOpen, setEditFilterOpen] = useState<boolean>(false);
    const [filters, setFilters] = useState<Filter[]>([]);
    const variant = PlaybookStore.useState(s => s.selectedTemplateVariant);
    const selectedFilter = PlaybookStore.useState(s => s.selectedFilter);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [row, setRow] = useState(null as null | Filter);

    const [filter, setFilter] = useState({
        id: uuid(),
        description: '',
        searchTerm: '',
        options: [],
        activityDate: 'today',        
        filterType: FilterType.Playbook
    } as Filter);

    useEffect(() => {
        loadSavedFilters();
    }, [])

    const loadSavedFilters = async () => {
        const response = await client.get('/api/leadfilters/cadence-template', { params: { filterType: FilterType.Playbook, variantId: variant?.id } });
        setFilters(response.data);
    }

    const onSearchTermChanged = (value: string) => {
        setFilter({ ...filter, searchTerm: value });
    }

    const onFilterChange = (newFilter: Filter) => {
        setFilter(newFilter);
    }

    const handleStartEdit = async (row: Filter) => {
        const response = await client.get(`/api/leadfilters/${row.id}`)
        setEditFilterOpen(true);
        PlaybookStore.update(s => {
            s.selectedFilter = response.data;
        })
    }

    const handleStartDelete = (row: Filter) => {
        setDeleteOpen(true);
        setRow(row);
    }

    const onCancelDelete = () => {
        setDeleteOpen(false);
        setRow(null);
    }


    const onDeleteFilter = async () => {
        const response = await client.delete('/api/leadfilters', { params: { id: row!.id } })
       // loadPlaybooks('');
        setDeleteOpen(false);
        setRow(null);
        await loadSavedFilters();
    }



    const columns = [
        ...defaultColumns,
        {
            flex: 0.15,
            minWidth: 115,
            sortable: false,
            field: 'actions',
            headerName: '',
            renderCell: ({ row }: CellType) => (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <RenderIf claimType={ClaimType.Playbooks} claimValue={ClaimValue.EditTemplateVariantFilters}>
                        <IconButton onClick={() => handleStartEdit(row)} title="Edit Filter">
                            <PencilOutline fontSize='small' />
                        </IconButton>
                    </RenderIf>
                    <RenderIf claimType={ClaimType.Playbooks} claimValue={ClaimValue.DeleteTemplateVariantFilters}>
                        <IconButton onClick={() => handleStartDelete(row)} title="Delete Filter">
                            <TrashCan fontSize='small' />
                        </IconButton>
                    </RenderIf>
                </Box>
            )
        }
    ]

    const onAddFilter = () => {
        setEditFilterOpen(true);
        PlaybookStore.update(s => {
            s.selectedFilter = {
                id: uuid(),
                options: [] as OptionWithSelect[]
            } as Filter
        })
    }

    const onCloseEditFilter = () => {
        setEditFilterOpen(false);

        PlaybookStore.update(s => {
            s.selectedFilter = null;
        })

        loadSavedFilters();
    }

    return (
        <SimpleDialog open={open} onClose={onClose}>
            <Box sx={{ margin: 5 }}>
                <Box sx={{ textAlign: 'left' }}>
                    <Grid container>
                        <Grid item xs={6}>
                            <PageHeader
                                title={<Typography variant='h5'>Filters for: {variant?.name}</Typography>}

                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>                                
                                <RenderIf claimType={ClaimType.Playbooks} claimValue={ClaimValue.AddTemplateVariantFilters}>
                                    <Box>
                                        <Button sx={{ mb: 2 }} variant='contained' onClick={() => onAddFilter()} startIcon={<AddIcon />} size="small">
                                            Add Filter
                                        </Button>
                                    </Box>
                                </RenderIf>
                                <Box>
                                    <Button sx={{ mb: 2, ml: 2 }} variant='contained' onClick={onClose} startIcon={<CloseIcon />} size="small">
                                        Close Filters
                                    </Button>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid xs={12}>
                            <DataGrid
                                sx={{ minHeight: '30vh' }}
                                rows={filters}
                                columns={columns}
                            />

                        </Grid>
                    </Grid>
                    {selectedFilter && editFilterOpen && <EditPlaybookFilter open={editFilterOpen} onClose={onCloseEditFilter} filter={selectedFilter} />}
                    <ConfirmationDialog open={deleteOpen} title="Delete Filter" message="Are you sure you want to delete the filter?" onCancel={onCancelDelete} onConfirm={onDeleteFilter} cancelButtonText="Cancel" confirmButtonText="Delete" />
                    
                </Box>
            </Box>
        </SimpleDialog>
    )
}

export { VariantFilters };
export default VariantFilters;